import axios from 'axios'
import PropTypes from 'prop-types'
import React, { useCallback, useState, useRef, useMemo } from 'react'
import Autosuggest from 'react-autosuggest'
import Highlighter from 'react-highlight-words'

import { captureException } from '@/utils/asyncSentry'

import { api } from '../../services'

/**
 * @pure
 */
function getSuggestionValue (suggestion) { return suggestion }

/**
 * @pure
 */
export const renderHighlightedSuggestion = (suggestion, { query, isHighlighted }) => {
  return (
    <Highlighter
      autoEscape
      searchWords={query.split(' ').map(w => w.trim()).filter(Boolean)}
      textToHighlight={suggestion}
      highlightClassName='search-highlight'
    />
  )
}

const SearchAutosuggest = ({
  searchType,
  onSuggestionSelected,
  renderSuggestion,
  fetchSuggestionsBlackList = [],
  shouldRenderSuggestions,
  predefinedSuggestions = [],
  label,
  ...props
}) => {
  const [proposedSuggestions, setProposedSuggestions] = useState([])
  const pendingRequest = useRef(null)

  const fetchSuggestions = useCallback((query, opts) => {
    const response = api.getAutocompleteSuggestions({
      term: query,
      type: searchType
    }, opts)
    const promise = response.then(response => response.data)
    promise.cancel = response.cancel
    return promise
  }, [searchType])

  const cancelPendingRequest = useCallback(() => {
    if (pendingRequest.current) {
      pendingRequest.current.cancel()
    }
  }, [])

  const handleSuggestionsFetchRequested = useCallback(async ({ value, reason }) => {
    cancelPendingRequest()
    try {
      if (!!value && (!fetchSuggestionsBlackList.length || !fetchSuggestionsBlackList.includes(value))) {
        pendingRequest.current = fetchSuggestions(value)
        const data = await pendingRequest.current
        setProposedSuggestions(data || [])
      }
    } catch (e) {
      if (!axios.isCancel(e)) {
        captureException(e)
      }
    }
  }, [cancelPendingRequest, fetchSuggestions, fetchSuggestionsBlackList])

  const handleSuggestionsClearRequested = useCallback(() => {
    cancelPendingRequest()
    setProposedSuggestions([])
  }, [cancelPendingRequest, setProposedSuggestions])

  const checkShouldRenderSuggestions = useCallback((value) => !!value && value.trim().length >= 2, [])

  const suggestions = useMemo(() =>
    [...predefinedSuggestions, ...proposedSuggestions]
  , [predefinedSuggestions, proposedSuggestions])

  const theme = useMemo(() => ({
    container: 'autosuggest',
    input: 'form-control',
    suggestionsContainer: 'dropdown',
    suggestionsList: `dropdown-menu ${suggestions.length ? 'show' : ''}`,
    suggestion: 'dropdown-item',
    suggestionHighlighted: 'active'
  }), [suggestions])

  return (
    <Autosuggest
      id={searchType}
      suggestions={suggestions}
      fetchSuggestions={fetchSuggestions}
      onSuggestionsFetchRequested={handleSuggestionsFetchRequested}
      onSuggestionsClearRequested={handleSuggestionsClearRequested}
      getSuggestionValue={getSuggestionValue}
      renderSuggestion={renderSuggestion || renderHighlightedSuggestion}
      onSuggestionSelected={onSuggestionSelected}
      focusInputOnSuggestionClick={false}
      inputProps={props}
      theme={theme}
      shouldRenderSuggestions={shouldRenderSuggestions || checkShouldRenderSuggestions}
    />
  )
}

SearchAutosuggest.propTypes = {
  searchType: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  onSuggestionSelected: PropTypes.func,
  label: PropTypes.string,
  shouldRenderSuggestions: PropTypes.func,
  predefinedSuggestions: PropTypes.arrayOf(PropTypes.any),
  renderSuggestion: PropTypes.func,
  fetchSuggestionsBlackList: PropTypes.arrayOf(PropTypes.any)
}

export default SearchAutosuggest
